import { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { getUserSeries, createEpisodeWithMedia } from '../../services/seriesService';
import { useToast } from '../../contexts/ToastContext';

const MAX_PAGES = 25;

const CreateEpisodeModal = ({ onClose, onCreated, initialSeriesId = '' }) => {
  const { showToast } = useToast();
  const { userData } = useAuth();

  const [seriesLoading, setSeriesLoading] = useState(true);
  const [mySeries, setMySeries] = useState([]);
  const [seriesId, setSeriesId] = useState('');

  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setSeriesLoading(true);
    (async () => {
      const res = await getUserSeries(userData?.id, 100);
      if (cancelled) return;
      const list = res.success ? (res.series || []) : [];
      setMySeries(list);
      const preferred = initialSeriesId && list.some((s) => s.id === initialSeriesId) ? initialSeriesId : (list[0]?.id || '');
      setSeriesId(preferred);
      setSeriesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userData?.id, initialSeriesId]);

  const canSubmit = useMemo(() => !!seriesId && selectedImages.length > 0 && !loading, [seriesId, selectedImages.length, loading]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [...selectedImages, ...files];
    if (next.length > MAX_PAGES) {
      setError(`Máximo ${MAX_PAGES} páginas por episodio`);
      return;
    }
    setError(null);
    setSelectedImages(next);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setPreviews((p) => [...p, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (idx) => {
    setSelectedImages((arr) => arr.filter((_, i) => i !== idx));
    setPreviews((arr) => arr.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!seriesId) {
      setError('Selecciona una serie');
      return;
    }
    if (selectedImages.length === 0) {
      setError('Añade al menos una página (imagen)');
      return;
    }
    setLoading(true);
    try {
      const res = await createEpisodeWithMedia({
        seriesId,
        title: title.trim(),
        caption: caption.trim(),
        files: selectedImages
      });
      if (!res.success) {
        setError(res.error || 'No se pudo crear el episodio');
        return;
      }
      showToast('Episodio publicado');
      onCreated?.({ seriesId, post: res.post, postId: res.postId, episodeNumber: res.episodeNumber });
      onClose?.();
    } catch (err) {
      setError(err.message || 'No se pudo crear el episodio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Crear episodio"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Crear episodio</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Serie *
              </label>
              <select
                value={seriesId}
                onChange={(e) => setSeriesId(e.target.value)}
                disabled={seriesLoading}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              >
                {mySeries.length === 0 ? (
                  <option value="">No tienes series aún</option>
                ) : (
                  mySeries.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))
                )}
              </select>
              {mySeries.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Primero crea una serie para poder publicar episodios.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Título (opcional)
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                maxLength={80}
                placeholder="Título del episodio"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{title.length}/80</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Texto (opcional)
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Descripción o nota del episodio"
              maxLength={400}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{caption.length}/400</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Páginas * (máximo {MAX_PAGES})
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="hidden"
              id="episode-pages"
            />
            <label
              htmlFor="episode-pages"
              className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors"
            >
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">Añadir páginas</p>
              </div>
            </label>

            {previews.length > 0 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {previews.map((preview, idx) => (
                  <div key={idx} className="relative aspect-[3/4] group">
                    <img src={preview} alt="" className="w-full h-full object-cover rounded-lg border border-gray-200 dark:border-slate-700" />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-slate-700 flex gap-3">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="gradient" className="flex-1" loading={loading} disabled={!canSubmit} onClick={handleSubmit}>
            Publicar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateEpisodeModal;

